import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon, ChevronDownIcon, SearchIcon, CheckIcon } from 'lucide-react';
import { InvoiceItem } from '../../../shared/types/Invoice';
export function InvoiceForm() {
  const {
    t
  } = useTranslation('invoices');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('id');
  const isEditMode = !!invoiceId;
  const [formData, setFormData] = useState({
    invoiceNumber: isEditMode ? 'INV-2024-001' : '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    description: '',
    notes: '',
    status: 'draft' as 'draft' | 'sent'
  });
  const [items, setItems] = useState<InvoiceItem[]>([{
    id: '1',
    description: '',
    quantity: 1,
    unitPrice: 0,
    total: 0
  }]);
  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const mockCustomers = [{
    id: '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+60 12 345 6789'
  }, {
    id: '2',
    name: 'Tech Solutions Ltd',
    email: 'accounts@techsolutions.mv',
    phone: '+60 11 234 5678'
  }, {
    id: '3',
    name: 'Global Enterprises',
    email: 'finance@global.com',
    phone: '+60 13 456 7890'
  }, {
    id: '4',
    name: 'Innovation Hub',
    email: 'billing@innovationhub.mv',
    phone: '+60 10 567 8901'
  }];
  const filteredCustomers = mockCustomers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()) || c.email.toLowerCase().includes(customerSearch.toLowerCase()));
  // Status dropdown state
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const statusOptions = [{
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'sent',
    label: 'Send to Customer'
  }];
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const calculateItemTotal = (quantity: number, unitPrice: number) => quantity * unitPrice;
  const calculateSubtotal = () => items.reduce((sum, item) => sum + item.total, 0);
  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setItems([...items, newItem]);
  };
  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };
  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = calculateItemTotal(updatedItem.quantity, updatedItem.unitPrice);
        }
        return updatedItem;
      }
      return item;
    }));
  };
  const selectCustomer = (customer: (typeof mockCustomers)[0]) => {
    setFormData({
      ...formData,
      customerName: customer.name,
      customerEmail: customer.email,
      customerPhone: customer.phone
    });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving invoice:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  const handleSaveAsDraft = () => {
    console.log('Saving as draft:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white';
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-7483" data-mp="1oy4r9hgwy9gn">
      {/* Header */}
      <div className="flex items-center" data-id="element-7484" data-mp="1kwj6l9y50qvw">
        <button onClick={() => navigate('/invoices')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-7485" data-mp="1kxkq82t7ylp8">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-7486" data-mp="28ilh48w5w0p4" />
          {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-7487" data-mp="1zi6rixc5zzft">
        {/* Invoice Details Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-7488" data-mp="1ktiwqta57tqx">
          <div className="p-6 border-b border-gray-100" data-id="element-7489" data-mp="1pk94ue9yeapb">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-7490" data-mp="1pltx1x8sphk0">
              {t('pages.InvoiceForm.invoice_details')}
            </h3>
          </div>
          <div className="p-6 space-y-5" data-id="element-7491" data-mp="1fvhgsh80kajo">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7492" data-mp="1fsd6d23ygikk">
              <div data-id="element-7493" data-mp="11vzldj07pz1c">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7494" data-mp="11vgdyw86ut1k">
                  {t('pages.InvoiceForm.invoice_number')}
                </label>
                <input type="text" value={formData.invoiceNumber} onChange={e => setFormData({
                ...formData,
                invoiceNumber: e.target.value
              })} placeholder={t('pages.InvoiceForm.attrs.autogenerated_if_left_empty')} className={inputClass} data-id="element-7495" data-mp="38jyws9pb965g" />
              </div>
              <div ref={statusDropdownRef} className="relative" data-id="element-7496" data-mp="3nhiwp27cob4r">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7497" data-mp="3iglh8xdq5v6y">
                  {t('pages.InvoiceForm.status')}
                </label>
                <button type="button" aria-expanded={showStatusDropdown} aria-haspopup="listbox" onClick={() => setShowStatusDropdown(!showStatusDropdown)} className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white text-left" data-id="element-7498" data-mp="3dhrz2rnhtg0g">
                  <span className="text-gray-900" data-id="element-7499" data-mp="21vjc2c0n6j0a">
                    {statusOptions.find(s => s.value === formData.status)?.label}
                  </span>
                  <ChevronDownIcon size={16} className={`text-gray-400 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} data-id="element-7500" data-mp="2ars6tddoctg3" />
                </button>
                {showStatusDropdown && <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1" data-id="element-7501" data-mp="2pm6jkw6nb6mb">
                    {statusOptions.map(option => <button key={option.value} type="button" onClick={() => {
                  setFormData({
                    ...formData,
                    status: option.value as 'draft' | 'sent'
                  });
                  setShowStatusDropdown(false);
                }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-7502" data-mp="v7vx2udiwojp">
                        <span data-id="element-7503" data-mp="2988r9a0h0sih">{option.label}</span>
                        {formData.status === option.value && <CheckIcon size={16} className="text-[#9F140F]" data-id="element-7504" data-mp="2n8ji4d1s673n" />}
                      </button>)}
                  </div>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7505" data-mp="2ghplz2cqx3p">
              <div data-id="element-7506" data-mp="3um4sgcgmpvhl">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7507" data-mp="3ukjyany88ek9">
                  {t('pages.InvoiceDetail.issue_date')}{' '}
                  <span className="text-red-500" data-id="element-7508" data-mp="61t6oglsjtl5">*</span>
                </label>
                <input type="date" required value={formData.issueDate} onChange={e => setFormData({
                ...formData,
                issueDate: e.target.value
              })} className={inputClass} data-id="element-7509" data-mp="b04j8z3h0i72" />
              </div>
              <div data-id="element-7510" data-mp="toj93j21xq6a">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7511" data-mp="tr5gnioy05oi">
                  {t('pages.InvoiceDetail.due_date')}{' '}
                  <span className="text-red-500" data-id="element-7512" data-mp="jypx4gxzz91i">*</span>
                </label>
                <input type="date" required value={formData.dueDate} onChange={e => setFormData({
                ...formData,
                dueDate: e.target.value
              })} className={inputClass} data-id="element-7513" data-mp="17pc568804tbi" />
              </div>
            </div>

            <div data-id="element-7514" data-mp="mdt3o86waa24">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7515" data-mp="mc88qhgo4p3q">
                {t('pages.InvoiceDetail.description')}
              </label>
              <input type="text" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} placeholder={t('pages.InvoiceForm.attrs.brief_description_of_the_invoice')} className={inputClass} data-id="element-7516" data-mp="cngm8oudmx07" />
            </div>
          </div>
        </div>

        {/* Customer Information Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-7517" data-mp="3usbhyrnfilu6">
          <div className="p-6 border-b border-gray-100" data-id="element-7518" data-mp="3b7jnudmh9fjx">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-7519" data-mp="375ps8jr531qm">
              {t('pages.InvoiceForm.customer_information')}
            </h3>
          </div>
          <div className="p-6 space-y-5" data-id="element-7520" data-mp="3g64crgwa4ct2">
            {/* Searchable Customer Select */}
            <div ref={customerDropdownRef} className="relative" data-id="element-7521" data-mp="3g8h42knxfzis">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7522" data-mp="3u4krnv4ap9ma">
                {t('pages.InvoiceForm.customer_name')}{' '}
                <span className="text-red-500" data-id="element-7523" data-mp="7qb7xzbb8pru">*</span>
              </label>
              <div className="relative" data-id="element-7524" data-mp="7pjeov56r44q">
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-7525" data-mp="2qpwwqiv8qyr" />

                <input type="text" required value={showCustomerDropdown ? customerSearch : formData.customerName || customerSearch} onChange={e => {
                setCustomerSearch(e.target.value);
                setFormData({
                  ...formData,
                  customerName: '',
                  customerEmail: '',
                  customerPhone: ''
                });
                setShowCustomerDropdown(true);
              }} onFocus={() => setShowCustomerDropdown(true)} placeholder={t('pages.InvoiceForm.attrs.search_or_enter_customer_name')} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-7526" data-mp="hm4gl7lwrz4c" />
              </div>
              {showCustomerDropdown && filteredCustomers.length > 0 && <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto" data-id="element-7527" data-mp="3bp9li8o1fzs8">
                  {filteredCustomers.map(customer => <button key={customer.id} type="button" onClick={() => selectCustomer(customer)} className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors" data-id="element-7528" data-mp="31z6lvpmevnkx">
                      <div data-id="element-7529" data-mp="3por426aqqgt5">
                        <div className="text-sm font-medium text-gray-900" data-id="element-7530" data-mp="3kowd62qytr83">
                          {customer.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-7531" data-mp="3fp08s2ecvxs0">
                          {customer.email}
                        </div>
                      </div>
                      {formData.customerName === customer.name && <CheckIcon size={16} className="text-[#9F140F] flex-shrink-0" data-id="element-7532" data-mp="24cr0wdus07sm" />}
                    </button>)}
                </div>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7533" data-mp="3j8h7c4da7y27">
              <div data-id="element-7534" data-mp="3o6sh6f3ku8kf">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7535" data-mp="3o60nj9tjikws">
                  {t('pages.InvoiceForm.email')}{' '}
                  <span className="text-red-500" data-id="element-7536" data-mp="kp0oeyhm9kl6">*</span>
                </label>
                <input type="email" required value={formData.customerEmail} onChange={e => setFormData({
                ...formData,
                customerEmail: e.target.value
              })} placeholder={t('pages.InvoiceForm.attrs.customerexamplecom')} className={inputClass} data-id="element-7537" data-mp="kmo6glbuza84" />
              </div>
              <div data-id="element-7538" data-mp="1dhm26ip2nsfv">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7539" data-mp="18kcdj59ibkt3">
                  {t('pages.InvoiceForm.phone')}
                </label>
                <input type="tel" value={formData.customerPhone} onChange={e => setFormData({
                ...formData,
                customerPhone: e.target.value
              })} placeholder="+60 12 345 6789" className={inputClass} data-id="element-7540" data-mp="1mittggt67t0g" />
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Items Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-7541" data-mp="ysznmj79r4pp">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between" data-id="element-7542" data-mp="b3f2btnn7vd9">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-7543" data-mp="72ld8wygsais">
              {t('pages.InvoiceForm.invoice_items')}
            </h3>
            <button type="button" onClick={addItem} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#9F140F] hover:bg-[#FEE7E5] rounded-lg transition-colors" data-id="element-7544" data-mp="2b6y1ti3d7xr8">
              <PlusIcon size={16} data-id="element-7545" data-mp="2kvprfjkye5yz" />
              {t('pages.InvoiceForm.add_item')}
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-7546" data-mp="1nb3hs0evlyl0">
            {items.map((item, index) => <div key={item.id} className="grid grid-cols-12 gap-3 items-start" data-id="element-7547" data-mp="1s8vfjb27cex4" data-mpi={index}>
                <div className="col-span-12 md:col-span-5" data-id="element-7548" data-mp="1x9svri3niyvz">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7549" data-mp="2246p7eyr6vy0">
                      {t('pages.InvoiceDetail.description')}
                    </label>}
                  <input type="text" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} placeholder={t('pages.InvoiceForm.attrs.item_description')} className={inputClass} data-id="element-7550" data-mp="3dnj636ld6je2" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-7551" data-mp="n7m581m8wudw">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7552" data-mp="i80vcw5jf1me">
                      {t('pages.InvoiceDetail.qty')}
                    </label>}
                  <input type="number" min="1" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', parseInt(e.target.value))} className={inputClass} data-id="element-7553" data-mp="9brv5f9qjyz7" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-7554" data-mp="17izpy8p1oapc">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7555" data-mp="1cim3kj8sbsyo">
                      {t('pages.InvoiceDetail.unit_price')}
                    </label>}
                  <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => updateItem(item.id, 'unitPrice', parseFloat(e.target.value))} className={inputClass} data-id="element-7556" data-mp="12s164z4ukuhc" />
                </div>
                <div className="col-span-3 md:col-span-2" data-id="element-7557" data-mp="298v3o5pmp6p2">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7558" data-mp="2e7g5g9kj3yfm">
                      {t('pages.InvoiceDetail.total')}
                    </label>}
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 font-medium" data-id="element-7559" data-mp="3tqntpajrcyn2">
                    {t('pages.InvoiceDetail.usd')}{' '}
                    {item.total.toLocaleString('en-US')}
                  </div>
                </div>
                <div className="col-span-1 flex items-end" data-id="element-7560" data-mp="7hct99stw8fg">
                  {index === 0 && <div className="mb-1.5 text-sm" data-id="element-7561" data-mp="l99in6344485">&nbsp;</div>}
                  <button type="button" onClick={() => removeItem(item.id)} disabled={items.length === 1} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-7562" data-mp="h9sdkdrll93i">
                    <TrashIcon size={18} data-id="element-7563" data-mp="2x0n4jafh8g58" />
                  </button>
                </div>
              </div>)}

            {/* Totals */}
            <div className="mt-6 pt-6 border-t border-gray-100" data-id="element-7564" data-mp="njy0opbvs5j0">
              <div className="flex justify-end" data-id="element-7565" data-mp="nid6x1xe4qm7">
                <div className="w-full max-w-xs space-y-2" data-id="element-7566" data-mp="ji2xtgeaxk34">
                  <div className="flex justify-between py-1.5" data-id="element-7567" data-mp="xa9h3bcr476s">
                    <span className="text-sm text-gray-500" data-id="element-7568" data-mp="x9r9jikn26jk">
                      {t('pages.InvoiceDetail.subtotal')}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-7569" data-mp="4lbxn4wt04cg">
                      {t('pages.InvoiceDetail.usd')}{' '}
                      {calculateSubtotal().toLocaleString('en-US')}
                    </span>
                  </div>
                  <div className="flex justify-between py-2.5 border-t-2 border-gray-200" data-id="element-7570" data-mp="elcln45mb8mz">
                    <span className="text-base font-semibold text-gray-900" data-id="element-7571" data-mp="9jdjfxlm6hrm">
                      {t('pages.InvoiceDetail.total')}
                    </span>
                    <span className="text-base font-bold text-gray-900" data-id="element-7572" data-mp="1l0n06cdcyvp8">
                      {t('pages.InvoiceDetail.usd')}{' '}
                      {calculateSubtotal().toLocaleString('en-US')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-7573" data-mp="2tz4vhd059ucz">
          <div className="p-6 border-b border-gray-100" data-id="element-7574" data-mp="2yy98keq6wc8l">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-7575" data-mp="3mmsggt5bun05">
              {t('pages.InvoiceDetail.notes')}
            </h3>
          </div>
          <div className="p-6" data-id="element-7576" data-mp="3cw63z7swqclo">
            <textarea value={formData.notes} onChange={e => setFormData({
            ...formData,
            notes: e.target.value
          })} rows={3} placeholder={t('pages.InvoiceForm.attrs.additional_notes_or_payment_terms')} className={`${inputClass} resize-none`} data-id="element-7577" data-mp="3cuv4hk6hvr0d" />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pb-8" data-id="element-7578" data-mp="j44nzvjv6x4n">
          <button type="button" onClick={() => navigate('/invoices')} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-7579" data-mp="e3gpypx7xg0x">
            {t('pages.InvoiceForm.cancel')}
          </button>
          <button type="button" onClick={handleSaveAsDraft} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-7580" data-mp="bsaqdroabfq">
            {t('pages.InvoiceForm.save_as_draft')}
          </button>
          <button type="submit" className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-7581" data-mp="3hkp1yt59llnq">
            {isEditMode ? 'Update Invoice' : 'Create Invoice'}
          </button>
        </div>
      </form>
    </div>;
}